import { Injectable } from '@angular/core';
import {
  map,
  distinctUntilChanged,
  Subject,
  debounceTime,
  filter,
  startWith,
  BehaviorSubject,
  Observable,
} from 'rxjs';
import { LoadingController } from '../../helpers/loading.controller';
import { MapNoteModelLike } from '../../models/map-note.model';
import { MapLayerVisibleLike, MapModelLike } from '../../models/map.model';
import { TocBasemapTypesDefault } from '../../models/toc-config';
import { ToolMapActionType } from '../../models/tool-action-type';
import { ToolMapType } from '../../models/tool-map-type';
import { WidgetModalType } from '../../models/widget-modal-type';
import { WidgetType } from '../../models/widget-type';
import { AppConfigService } from '../../services/app-config.service';
import { AppTrackingService } from '../../services/app-tracking.service';
import { EsriUtils } from '../../utils/esri.utils';
import { ObjectUtils } from '../../utils/object.utils';

export type GoToTarget =
  | __esri.Geometry
  | __esri.Geometry[]
  | __esri.Collection<__esri.Geometry>
  | __esri.Graphic
  | __esri.Graphic[]
  | __esri.Collection<__esri.Graphic>
  | __esri.Viewpoint;

const toolMapTypeSingleList = [
  ToolMapType.Identify,
  ToolMapType.CurrentLocation,
  ToolMapType.StreetView,
  ToolMapType.ZoomByWindow,
  ToolMapType.AvailableCoverage,
];

const toolMapTypeListCannotTrackLog = [ToolMapType.Identify];

@Injectable()
export class MainMapService {
  private _viewpointSubject = new Subject<__esri.Viewpoint>();

  private _widgetModalSubject = new Subject<{
    type: WidgetModalType;
    opened: boolean;
  }>();
  private _widgetModalMinimizeSubject = new Subject<{
    type: WidgetModalType;
    minimized: boolean;
  }>();

  private _toolMapSubject = new Subject<{
    type: ToolMapType;
    active: boolean;
  }>();

  private _widgetMapSubject = new Subject<{
    type: WidgetType;
    active: boolean;
  }>();

  private _toolMapActionSubject = new Subject<ToolMapActionType>();

  private mainImageLayerNotVisibleController = new LoadingController();
  get mainImageLayerVisibilityChanges() {
    return this.mainImageLayerNotVisibleController.changes.pipe(map((x) => !x));
  }

  private mapPopupNotActiveController = new LoadingController();
  get mapPopupEnabledChanges() {
    return this.mapPopupNotActiveController.changes.pipe(map((x) => !x));
  }

  canZoomIn = true;
  canZoomOut = true;
  private _goToSubject = new Subject<{
    target: GoToTarget;
    options?: __esri.GoToOptions2D;
  }>();

  private viewpointIndex = -1;
  private _viewpointList: __esri.Viewpoint[] = [];

  private _basemapId: string = TocBasemapTypesDefault.Default;
  private _basemapIdSubject = new Subject<string>();

  private _layerVisibleMap = new Map<string, MapLayerVisibleLike>();
  private _layerVisibleListSubject = new Subject<MapLayerVisibleLike[]>();

  private _notesLoadSubject = new BehaviorSubject<MapNoteModelLike[]>([]);

  private _mapId: string;

  private widgetModalActiveMap = new Map<
    WidgetModalType,
    {
      minimized?: boolean;
    }
  >();
  private toolMapActiveSet = new Set<ToolMapType>();
  private widgetMapActiveSet = new Set<WidgetType>([
    WidgetType.Coords,
    WidgetType.Scales,
  ]);

  get toolMapChanges() {
    return this._toolMapSubject.asObservable().pipe(debounceTime(0));
  }

  get widgetModalActiveList() {
    return Array.from(this.widgetModalActiveMap.keys());
  }

  get onWidgetModalToggle() {
    return this._widgetModalSubject.asObservable();
  }

  get onWidgetModalMinimized() {
    return this._widgetModalMinimizeSubject.asObservable();
  }

  get widgetActiveList() {
    return Array.from(this.widgetMapActiveSet);
  }

  get onWidgetToggle() {
    return this._widgetMapSubject.asObservable();
  }

  get onRequestToolAction() {
    return this._toolMapActionSubject.asObservable();
  }

  get onRequestClear() {
    return this.onRequestToolAction.pipe(
      filter((x) => x === ToolMapActionType.Clear)
    );
  }

  get onZoomIn() {
    return this.onRequestToolAction.pipe(
      filter((x) => x === ToolMapActionType.ZoomIn)
    );
  }

  get onZoomOut() {
    return this.onRequestToolAction.pipe(
      filter((x) => x === ToolMapActionType.ZoomOut)
    );
  }

  get onGoTo() {
    return this._goToSubject.asObservable();
  }

  get onViewpointChanged() {
    return this._viewpointSubject.asObservable();
  }

  get onBasemapIdChanged() {
    return this._basemapIdSubject.asObservable();
  }

  get onLayerVisibleListChanges() {
    return this._layerVisibleListSubject.asObservable();
  }

  get onNotesLoad() {
    return this._notesLoadSubject.asObservable();
  }

  constructor(
    private appConfig: AppConfigService,
    private appTrackingService: AppTrackingService
  ) {}

  isMapLoaded(map: MapModelLike) {
    return this.isMapLoadedById(map.id);
  }

  isMapLoadedById(mapId: string) {
    return this._mapId === mapId;
  }

  loadMap(map: MapModelLike) {
    this._mapId = map.id;

    this.setBasemapId(map.basemapId);
    this.setViewpoint(map.viewPoint);
    this.setLayerVisibleList(map.layers);

    this._notesLoadSubject.next(map.notes?.slice() ?? []);
  }

  setLayerVisibleList(layerVisibleList: MapLayerVisibleLike[]) {
    const map = new Map<string, MapLayerVisibleLike>(
      layerVisibleList?.map((x) => {
        return [x.layer, ObjectUtils.clone(x)];
      }) ?? []
    );

    const changesSet = new Set<string>();

    for (let [layer, x] of map) {
      if (this._layerVisibleMap.has(layer)) {
        if (this._layerVisibleMap.get(layer)?.visible !== x.visible) {
          changesSet.add(layer);
        }
      } else {
        changesSet.add(layer);
      }
    }

    const changesList = Array.from(changesSet, (layer) => {
      const item = map.get(layer);
      return {
        id: item?.id,
        layer,
        visible: item?.visible,
      };
    });

    for (let [layer, x] of this._layerVisibleMap) {
      if (!map.has(layer) && x?.visible) {
        changesList.push({
          id: x?.id,
          layer,
          visible: false,
        });
      }
    }

    this._layerVisibleMap = map;

    this._layerVisibleListSubject.next(changesList);
  }

  changeLayerVisible(layer: string, visible: boolean) {
    visible = !!visible;
    let item = this._layerVisibleMap.get(layer);
    if (item?.visible !== visible) {
      if (!item) {
        item = { layer, visible };
        this._layerVisibleMap.set(layer, item);
      } else {
        item.visible = visible;
      }

      this._layerVisibleListSubject.next([ObjectUtils.clone(item)]);
    }
  }

  isLayerVisible(layer: string): boolean {
    return this._layerVisibleMap.get(layer)?.visible;
  }

  getCurrentLayerVisibleList(): MapLayerVisibleLike[] {
    return Array.from(this._layerVisibleMap, ([, item]) => {
      return ObjectUtils.clone(item);
    });
  }

  getBasemapId() {
    return this._basemapId;
  }

  setBasemapId(basemapId: string) {
    if (this._basemapId !== basemapId) {
      this._basemapId = basemapId;
      this._basemapIdSubject.next(this._basemapId);
    }
  }

  setViewpoint(viewpoint: __esri.Viewpoint) {
    if (!viewpoint) {
      return;
    }

    if (
      EsriUtils.isViewpointEquals(
        this._viewpointList[this.viewpointIndex],
        viewpoint
      )
    ) {
      return;
    }

    viewpoint = viewpoint.clone();
    this._viewpointList.splice(++this.viewpointIndex);
    this._viewpointList.push(viewpoint);
    this._viewpointSubject.next(viewpoint);
  }

  initialViewpoint() {
    this.requestToolMapAction(ToolMapActionType.InitialViewpoint);
  }

  previousViewpoint() {
    this.requestToolMapAction(ToolMapActionType.PreviousViewpoint);
  }

  nextViewpoint() {
    this.requestToolMapAction(ToolMapActionType.NextViewpoint);
  }

  getCurrentViewpoint() {
    return this._viewpointList[this.viewpointIndex];
  }

  zoomIn() {
    this.requestToolMapAction(ToolMapActionType.ZoomIn);
  }

  zoomOut() {
    this.requestToolMapAction(ToolMapActionType.ZoomOut);
  }

  goTo(target: GoToTarget, options?: __esri.GoToOptions2D) {
    this._goToSubject.next({ target, options });
  }

  canRequestToolMapAction(type: ToolMapActionType) {
    switch (type) {
      case ToolMapActionType.PreviousViewpoint:
        return this._canPreviousViewpointInternal();
      case ToolMapActionType.NextViewpoint:
        return this._canNextViewpointInternal();
      case ToolMapActionType.ZoomIn:
        return !!this.canZoomIn;
      case ToolMapActionType.ZoomOut:
        return !!this.canZoomOut;
    }

    return true;
  }

  requestToolMapAction(type: ToolMapActionType) {
    if (!this.canRequestToolMapAction(type)) {
      return;
    }

    switch (type) {
      case ToolMapActionType.InitialViewpoint:
        this.setViewpoint(this._viewpointList[0]);
        break;
      case ToolMapActionType.PreviousViewpoint:
        this._previousViewpointInternal();
        break;
      case ToolMapActionType.NextViewpoint:
        this._nextViewpointInternal();
        break;
    }

    this.disableAllSingleTools();
    this._toolMapActionSubject.next(type);
    this.appTrackingService.sendToolMapAction(type);
  }

  toggleWidgetModal(type: WidgetModalType) {
    if (!this.isWidgetModalActive(type)) {
      this.openWidgetModal(type);
    } else {
      this.closeWidgetModal(type);
    }
  }

  openWidgetModal(type: WidgetModalType) {
    if (!this.isWidgetModalActive(type)) {
      this.disableAllSingleTools();

      this.widgetModalActiveMap.set(type, {});

      this._widgetModalSubject.next({ type, opened: true });
      this.appTrackingService.sendWidgetModal(type);
    }
  }

  closeWidgetModal(type: WidgetModalType) {
    if (this.isWidgetModalActive(type)) {
      this.widgetModalActiveMap.delete(type);

      this._widgetModalSubject.next({ type, opened: false });
    }
  }

  isWidgetModalActive(type: WidgetModalType) {
    return this.widgetModalActiveMap.has(type);
  }

  minimizeWidgetModal(type: WidgetModalType) {
    if (!this.isWidgetModalMinimized(type)) {
      const aux = this.widgetModalActiveMap.get(type);
      aux.minimized = true;

      this._widgetModalMinimizeSubject.next({ type, minimized: aux.minimized });
    }
  }

  maximizeWidgetModal(type: WidgetModalType) {
    if (this.isWidgetModalMinimized(type)) {
      const aux = this.widgetModalActiveMap.get(type);
      aux.minimized = false;

      this._widgetModalMinimizeSubject.next({ type, minimized: aux.minimized });
    }
  }

  isWidgetModalMinimized(type: WidgetModalType) {
    return !!this.widgetModalActiveMap.get(type)?.minimized;
  }

  toggleWidget(type: WidgetType) {
    if (!this.isWidgetActive(type)) {
      this.openWidget(type);
    } else {
      this.closeWidget(type);
    }
  }

  openWidget(type: WidgetType) {
    if (!this.isWidgetActive(type)) {
      this.disableAllSingleTools();

      this.widgetMapActiveSet.add(type);

      this._widgetMapSubject.next({ type, active: true });
      this.appTrackingService.sendWidgetMap(type);
    }
  }

  closeWidget(type: WidgetType) {
    if (this.isWidgetActive(type)) {
      this.widgetMapActiveSet.delete(type);

      this._widgetMapSubject.next({ type, active: false });
    }
  }

  isWidgetActive(type: WidgetType) {
    return this.widgetMapActiveSet.has(type);
  }

  onToolMapChanged(): Observable<{ type: ToolMapType; active: boolean }>;
  onToolMapChanged(type: ToolMapType): Observable<boolean>;
  onToolMapChanged(type?: ToolMapType) {
    if (type === null || type === undefined) {
      return this._toolMapSubject.asObservable();
    }

    return this._toolMapSubject.asObservable().pipe(
      filter((x) => x.type === type),
      map((x) => x.active),
      startWith(this.isToolMapActive(type)),
      distinctUntilChanged(),
      debounceTime(0)
    );
  }

  isToolMapActive(type: ToolMapType) {
    return this.toolMapActiveSet.has(type);
  }

  hasAnyToolMapActive() {
    return this.toolMapActiveSet.size > 0;
  }

  toggleToolMap(type: ToolMapType) {
    if (this.isToolMapActive(type)) {
      this.disableToolMap(type);
    } else {
      this.enableToolMap(type);
    }
  }

  enableToolMap(type: ToolMapType) {
    if (this.isToolMapActive(type)) {
      return;
    }

    this.disableAllSingleTools();
    this.toolMapActiveSet.add(type);
    this._toolMapSubject.next({ type, active: true });

    if (!toolMapTypeListCannotTrackLog.includes(type)) {
      this.appTrackingService.sendToolMap(type);
    }
  }

  disableAllSingleTools() {
    for (const type of toolMapTypeSingleList) {
      this.disableToolMap(type);
    }
  }

  disableToolMap(type: ToolMapType) {
    if (this.isToolMapActive(type)) {
      this.toolMapActiveSet.delete(type);
      this._toolMapSubject.next({ type, active: false });
    }
  }

  showMainImageLayer() {
    this.mainImageLayerNotVisibleController.hide();
  }

  hideMainImageLayer() {
    this.mainImageLayerNotVisibleController.show();
  }

  enableMapPopup() {
    this.mapPopupNotActiveController.hide();
  }

  disableMapPopup() {
    this.mapPopupNotActiveController.show();
  }

  openStreetMapWindow(lat: number, long: number) {
    const url = `
      https://maps.google.com/maps?f=q&hl=en&geocode=&sll=${lat},${long}&sspn=0.007104,0.020084&layer=c&ie=UTF8&ll=${lat},${long}&spn=0.007104,0.027122&z=16&cbll=${lat},${long}&cbp=2,142.0851621378081,,0,3.3364732946064946
    `;

    window.open(url)?.focus();

    this.disableToolMap(ToolMapType.StreetView);
  }

  private _canPreviousViewpointInternal() {
    return !!(this.viewpointIndex > 0 && this._viewpointList.length);
  }

  private _canNextViewpointInternal() {
    return !!(
      this.viewpointIndex < this._viewpointList.length - 1 &&
      this._viewpointList.length
    );
  }

  private _previousViewpointInternal() {
    --this.viewpointIndex;
    this._viewpointSubject.next(this._viewpointList[this.viewpointIndex]);
  }

  private _nextViewpointInternal() {
    ++this.viewpointIndex;
    this._viewpointSubject.next(this._viewpointList[this.viewpointIndex]);
  }
}
